import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  FiBookOpen,
  FiChevronRight,
  FiInfo,
  FiSearch
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import { accountAPI } from '../utils/api';
import { guideBadgeClasses, guideCommands, guideCopy } from '../data/telegramGuideData';

const roleLabels = {
  system: 'System',
  super_admin: 'Super Admin',
  reseller: 'Reseller',
  user: 'User'
};

const TelegramGuide = () => {
  const [accountRole, setAccountRole] = useState('user');
  const [language, setLanguage] = useState('en');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchAccount = async () => {
      try {
        const response = await accountAPI.getMe();
        if (response.data?.success) {
          setAccountRole(response.data.data?.role || 'user');
        }
      } catch (error) {
        console.error('Error loading guide role:', error);
        toast.error('Failed to load guide information');
      }
    };

    fetchAccount();
  }, []);

  const copy = guideCopy[language];

  const filteredCommands = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    return guideCommands.filter((command) => {
      if (!command.roles.includes(accountRole)) return false;
      if (!query) return true;

      const haystack = [
        command.name,
        command.description.en,
        command.description.my,
        command.scope.en,
        command.scope.my,
        command.location.en,
        command.location.my,
        command.usage,
        ...command.examples
      ].join(' ').toLowerCase();

      return haystack.includes(query);
    });
  }, [accountRole, searchTerm]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <motion.section
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden rounded-[30px] border border-sky-100 bg-gradient-to-br from-sky-600 via-blue-600 to-indigo-700 p-6 text-white shadow-[0_30px_80px_rgba(29,78,216,0.28)] dark:border-sky-500/20"
        >
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.2),transparent_28%),radial-gradient(circle_at_bottom_left,rgba(255,255,255,0.12),transparent_30%)]" />
          <div className="relative space-y-4">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.24em] text-sky-50">
              <FiBookOpen className="h-4 w-4" />
              Role: {roleLabels[accountRole] || 'User'}
            </span>
            <div className="space-y-3">
              <h1 className="text-3xl font-black tracking-tight sm:text-4xl">{copy.title}</h1>
              <p className="max-w-3xl text-sm leading-7 text-sky-50/90 sm:text-base">{copy.subtitle}</p>
            </div>
          </div>
        </motion.section>

        <section className="mt-6 rounded-[28px] border border-white/80 bg-white p-5 shadow-[0_24px_60px_rgba(15,23,42,0.08)] dark:border-gray-800 dark:bg-gray-900">
          <div className="grid gap-4 lg:grid-cols-[1fr,auto]">
            <label className="space-y-2">
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">{copy.search}</span>
              <div className="flex items-center gap-3 rounded-2xl border border-sky-100 bg-sky-50/70 px-4 py-3 dark:border-sky-900/40 dark:bg-sky-950/20">
                <FiSearch className="h-4 w-4 text-sky-500" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder={copy.searchPlaceholder}
                  className="w-full border-0 bg-transparent text-sm text-gray-900 outline-none placeholder:text-gray-400 dark:text-white dark:placeholder:text-gray-500"
                />
              </div>
            </label>

            <div className="space-y-2">
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">{copy.language}</span>
              <div className="inline-flex rounded-2xl border border-sky-100 bg-sky-50 p-1 dark:border-sky-900/40 dark:bg-sky-950/20">
                {Object.entries(copy.languageOptions).map(([value, label]) => {
                  const active = language === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setLanguage(value)}
                      className={`rounded-2xl px-4 py-2 text-sm font-semibold transition-all ${
                        active
                          ? 'bg-gradient-to-r from-sky-500 to-indigo-500 text-white shadow-lg'
                          : 'text-gray-600 hover:text-sky-600 dark:text-gray-300 dark:hover:text-sky-300'
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        <section className="mt-8 space-y-5">
          {!filteredCommands.length ? (
            <div className="rounded-[26px] border border-dashed border-sky-200 bg-white px-6 py-12 text-center shadow-sm dark:border-sky-900/40 dark:bg-gray-900">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-sky-100 text-sky-600 dark:bg-sky-900/30 dark:text-sky-300">
                <FiSearch className="h-6 w-6" />
              </div>
              <p className="mt-4 text-lg font-bold text-gray-900 dark:text-white">{copy.empty}</p>
            </div>
          ) : (
            filteredCommands.map((command, index) => (
              <motion.article
                key={command.id}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
                className="rounded-[28px] border border-white/80 bg-white p-5 shadow-[0_22px_55px_rgba(15,23,42,0.06)] dark:border-gray-800 dark:bg-gray-900"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-2xl font-black tracking-tight text-gray-900 dark:text-white">{command.name}</p>
                    <p className="mt-3 max-w-3xl text-sm leading-7 text-gray-600 dark:text-gray-300">{command.description[language]}</p>
                  </div>
                  <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${guideBadgeClasses[command.badge]}`}>
                    {command.location[language]}
                  </span>
                </div>

                <div className="mt-5 grid gap-4 xl:grid-cols-[1fr,0.95fr]">
                  <div className="rounded-2xl border border-slate-200 bg-slate-950 px-4 py-4 text-slate-100 dark:border-gray-700">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">{copy.usage}</p>
                    <pre className="mt-2 overflow-x-auto whitespace-pre-wrap font-mono text-[13px] leading-6 text-slate-100">{command.usage}</pre>
                  </div>
                  <div className="rounded-2xl bg-slate-50 p-4 dark:bg-gray-800/70">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">{copy.scope}</p>
                    <p className="mt-2 text-sm font-medium leading-6 text-gray-800 dark:text-gray-200">{command.scope[language]}</p>
                  </div>
                </div>

                <div className="mt-5 rounded-2xl border border-sky-100 bg-sky-50 px-4 py-4 dark:border-sky-900/40 dark:bg-sky-950/20">
                  <div className="flex items-center gap-2 text-sm font-semibold text-sky-700 dark:text-sky-300">
                    <FiChevronRight className="h-4 w-4" />
                    {copy.examples}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {command.examples.map((example) => (
                      <span
                        key={example}
                        className="inline-flex rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-sky-700 shadow-sm dark:bg-gray-900 dark:text-sky-300"
                      >
                        {example}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.article>
            ))
          )}
        </section>

        <section className="mt-8 rounded-[28px] border border-white/80 bg-white p-6 shadow-[0_20px_55px_rgba(15,23,42,0.06)] dark:border-gray-800 dark:bg-gray-900">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-300">
              <FiInfo className="h-5 w-5" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">{copy.notesTitle}</h2>
          </div>

          <div className="mt-5 grid gap-3 md:grid-cols-3">
            {copy.notes.map((note) => (
              <div
                key={note}
                className="rounded-2xl border border-slate-100 bg-slate-50 p-4 text-sm leading-6 text-gray-700 dark:border-gray-800 dark:bg-gray-800/60 dark:text-gray-200"
              >
                {note}
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default TelegramGuide;
